// Telefon numarası için tek doğruluk kaynağı (web).
// backend/Flexbee.Api/Common/PhoneHelper.cs ve mobile/src/utils/phone.js ile
// birebir aynı kuralları uygular.
//
// Kanonik biçim:
//   areaCode    = ülke/arama kodu, YALNIZCA rakam ("1", "90") — "+" yok.
//   phoneNumber = ulusal numara; arama kodunu İÇERMEZ.
//
// Neden var: react-international-phone onChange'de E.164 değer üretir
// ("+905558733768"); bu değer olduğu gibi phoneNumber'a yazılıyor, arama kodu
// ayrıca areaCode'a gidiyordu. Gösterimde başına bir kez daha "+areaCode"
// eklenince "+90 +905558733768" / "+1 +1604754125" çıkıyordu. Bu yüzden hem
// yazma (normalizePhoneParts/toE164) hem gösterim (formatPhone) tek yerden
// geçer; geçmişte bozulmuş satırlar da okuma anında doğru gösterilir.

const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '');

// Baştaki `count` haneyi ve hemen ardından gelen ayraçları atar; numaranın
// kendi biçimlendirmesi ("555 873 37 68") korunur.
const stripLeadingDigits = (value, count) => {
  let removed = 0;
  let i = 0;
  for (; i < value.length && removed < count; i += 1) {
    if (/\d/.test(value[i])) removed += 1;
  }
  return value.slice(i).replace(/^[\s\-().]+/, '');
};

// Kanonik parçalar. Girdi zaten temizse aynen döner (bilgi kaybı yok).
export const normalizePhoneParts = (areaCode, phoneNumber) => {
  const area = digitsOnly(areaCode);
  const raw = String(phoneNumber ?? '').trim();

  if (!raw) return { areaCode: area, phoneNumber: '' };

  if (raw.startsWith('+')) {
    // "+" başlangıcı, baştaki hanelerin arama kodu olduğunu kesinleştirir.
    const rest = raw.slice(1).replace(/^\s+/, '');
    const restDigits = digitsOnly(rest);

    if (area && restDigits.startsWith(area)) {
      // Yalnızca arama kodu yazılmış (ör. "+1") — ulusal kısım boş.
      if (restDigits.length === area.length) return { areaCode: area, phoneNumber: '' };
      return { areaCode: area, phoneNumber: stripLeadingDigits(rest, area.length) };
    }

    // Arama kodu bilinmiyor: tahmin yürütmek yanlış numara üretir. Değeri
    // E.164 olarak bırakırız; formatPhone "+" ile başlayanı olduğu gibi basar.
    return { areaCode: area, phoneNumber: raw };
  }

  // "+" YOKSA değer tanım gereği ulusal numaradır — dokunmayız. Sezgisel
  // "numara arama koduyla başlıyorsa ayıkla" kuralı BİLEREK uygulanmıyor:
  // Kazakistan arama kodu "7" ve KZ cep numaralarının ulusal kısmı da 7 ile
  // başlıyor (701/705/707/708/747/771/775/776/777/778...), yani böyle bir kural
  // kanonik saklanmış her Kazak numarasını budardı. Çiftlenme her zaman E.164
  // değerinden geliyor; onu da yukarıdaki "+" dalı kesin biçimde çözüyor.
  return { areaCode: area, phoneNumber: raw };
};

// Ekran gösterimi: "+90 5558733768". Numara yoksa `fallback`.
export const formatPhone = (areaCode, phoneNumber, fallback = '-') => {
  const { areaCode: area, phoneNumber: national } = normalizePhoneParts(areaCode, phoneNumber);
  if (!national) return fallback;
  if (national.startsWith('+')) return national;
  return area ? `+${area} ${national}` : national;
};

// Makine biçimi (telefon girişi bileşeninin değeri, tel: bağlantısı):
// "+905558733768".
export const toE164 = (areaCode, phoneNumber) => {
  const { areaCode: area, phoneNumber: national } = normalizePhoneParts(areaCode, phoneNumber);
  if (!national) return area ? `+${area}` : '';
  if (national.startsWith('+')) return `+${digitsOnly(national)}`;
  const digits = digitsOnly(national);
  return area ? `+${area}${digits}` : digits;
};
